import React, { createContext, useContext, useEffect } from 'react';
import { useThemeRotation, ThemeRotationState } from './useThemeRotation';
import { ThemeTokens, defaultTheme } from './stylesRegistry';
import ShaderBackground from '../components/ShaderBackground';
import ThemeController from '../components/ThemeController';

// ============================================
// CONTEXT
// ============================================
interface ThemeContextValue {
  theme: ThemeTokens;
  state: ThemeRotationState;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================
// HOOK TO USE THEME
// ============================================
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default theme if not in provider (fallback)
    return {
      theme: defaultTheme,
      state: {} as ThemeRotationState,
    };
  }
  return context;
};

// ============================================
// CSS VARIABLES HELPER
// ============================================
const applyCSSVariables = (theme: ThemeTokens) => {
  const root = document.documentElement;

  // Background
  root.style.setProperty('--theme-overlay', theme.background.overlay);
  root.style.setProperty('--theme-overlay-strength', String(theme.background.overlayStrength));

  // Text
  root.style.setProperty('--theme-text-primary', theme.text.primary);
  root.style.setProperty('--theme-text-secondary', theme.text.secondary);
  root.style.setProperty('--theme-text-muted', theme.text.muted);
  root.style.setProperty('--theme-text-accent', theme.text.accent);

  // Panel
  root.style.setProperty('--theme-panel-bg', theme.panel.bg);
  root.style.setProperty('--theme-panel-border', theme.panel.border);
  root.style.setProperty('--theme-panel-shadow', theme.panel.shadow);

  // Button primary
  root.style.setProperty('--theme-btn-primary-bg', theme.button.primary.bg);
  root.style.setProperty('--theme-btn-primary-text', theme.button.primary.text);
  root.style.setProperty('--theme-btn-primary-hover', theme.button.primary.hover);

  // Button secondary
  root.style.setProperty('--theme-btn-secondary-bg', theme.button.secondary.bg);
  root.style.setProperty('--theme-btn-secondary-text', theme.button.secondary.text);
  root.style.setProperty('--theme-btn-secondary-hover', theme.button.secondary.hover);

  // Fonts
  root.style.setProperty('--theme-font-heading', theme.fonts.heading);
  root.style.setProperty('--theme-font-body', theme.fonts.body);
  root.style.setProperty('--theme-font-mono', theme.fonts.mono);

  // Radius
  root.style.setProperty('--theme-radius-sm', theme.radius.sm);
  root.style.setProperty('--theme-radius-md', theme.radius.md);
  root.style.setProperty('--theme-radius-lg', theme.radius.lg);

  // Mood (for conditional styling)
  root.style.setProperty('--theme-mood', theme.mood);
};

// ============================================
// PROVIDER COMPONENT
// ============================================
interface ThemeProviderProps {
  children: React.ReactNode;
  showController?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  showController = true,
}) => {
  const state = useThemeRotation();
  const { currentStyle, currentPreset, currentTheme } = state;

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    applyCSSVariables(currentTheme);
  }, [currentTheme]);

  // Expose current style id for style-specific global CSS overrides
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-style-id', currentStyle.id);
  }, [currentStyle.id]);

  const contextValue: ThemeContextValue = {
    theme: currentTheme,
    state,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {/* Shader background */}
      <ShaderBackground
        style={currentStyle}
        preset={currentPreset}
        theme={currentTheme}
      />

      {/* Theme controller UI */}
      {showController && <ThemeController state={state} />}

      {/* App content */}
      <div className="relative z-10">
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
